/**
 * Generic status history + transition helper for SCM entities.
 * Collection: statusHistory
 */

import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  runTransaction
} from "./fs";
import { db } from "../config/firebase";
import { AuditService } from "./firestore";
import { listOwned } from "./ownership";

export const StatusHistoryService = {
  async list(entityType, entityId, { limitCount = 100 } = {}) {
    return listOwned("statusHistory", {
      filters: [["entityType", entityType], ["entityId", entityId]],
      max: limitCount,
      includeDeleted: true
    });
  },

  async append({
    entityType,
    entityId,
    previousStatus,
    newStatus,
    userId,
    note = null,
    meta = {}
  }) {
    const payload = {
      entityType,
      entityId,
      previousStatus: previousStatus ?? null,
      newStatus,
      userId: userId || null,
      note,
      meta,
      createdAt: serverTimestamp()
    };
    const ref = await addDoc(collection(db, "statusHistory"), payload);
    return { id: ref.id, ...payload };
  },

  /**
   * Atomically update entity.status and append history.
   * collectionName: Firestore collection of the entity document.
   * allowedTransitions: optional map { fromStatus: [toStatus, ...] }
   */
  async transitionStatus({
    collectionName,
    entityId,
    newStatus,
    userId,
    note = null,
    allowedTransitions = null,
    entityType = null
  }) {
    const type = entityType || collectionName;
    const entityRef = doc(db, collectionName, entityId);

    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(entityRef);
      if (!snap.exists()) throw new Error("ENTITY_NOT_FOUND");
      const data = snap.data();
      const previousStatus = data.status ?? null;

      if (previousStatus === newStatus) {
        return { skipped: true, previousStatus, newStatus };
      }

      if (allowedTransitions && previousStatus != null) {
        const allowed = allowedTransitions[previousStatus] || [];
        if (!allowed.includes(newStatus)) {
          throw new Error("INVALID_STATUS_TRANSITION");
        }
      }

      tx.update(entityRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: userId || null
      });

      return { skipped: false, previousStatus, newStatus };
    });

    if (!result.skipped) {
      await this.append({
        entityType: type,
        entityId,
        previousStatus: result.previousStatus,
        newStatus: result.newStatus,
        userId,
        note
      });
      await AuditService.logChange({
        userId,
        action: "STATUS_TRANSITION",
        entityType: type,
        entityId,
        previousValue: { status: result.previousStatus },
        newValue: { status: result.newStatus, note }
      });
    }

    return result;
  }
};
